import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import ReactPaginate from "react-paginate";
import axiosInstance from "../../config/ProtectedAxios";
import { FaUsers, FaCheckCircle, FaDollarSign, FaSearch, FaTimes } from "react-icons/fa";
import useDebounce from "../../hooks/useDebounce";

const defaultFilters = {
  status: "",
  startDate: "",
  endDate: "",
  subscriberId: "",
  organizationId: "",
  paymentMethod: "",
  minAmount: "",
  maxAmount: "",
};

const limitOptions = [10, 25, 50, 100];

const statusOptions = [
  { label: "All statuses", value: "" },
  { label: "Active", value: "active" },
  { label: "Pending", value: "pending" },
  { label: "Paused", value: "paused" },
  { label: "Cancelled", value: "canceled" },
  { label: "Expired", value: "expired" },
];


const Subscription = () => {
  const userToken = localStorage.getItem("userToken");
  const protectedAxios = useMemo(
    () => (userToken ? axiosInstance(userToken) : null),
    [userToken]
  );

  const [formFilters, setFormFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(limitOptions[0]);
  const [data, setData] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: limitOptions[0],
    total: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const hasActiveFilters = useMemo(() => {
    return Object.values(appliedFilters).some((value) => value);
  }, [appliedFilters]);

  useEffect(() => {
    if (!protectedAxios) {
      setError("Missing user token. Please log in again.");
      return;
    }
    fetchSubscriptions();
  }, [page, limit, appliedFilters, protectedAxios]);

  const buildQueryParams = () => {
    const params = { page, limit };

    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (value !== "" && value !== null) {
        if (["minAmount", "maxAmount"].includes(key)) {
          params[key] = Number(value);
        } else {
          params[key] = value;
        }
      }
    });

    return params;
  };

  const fetchSubscriptions = async () => {
    if (!protectedAxios) {
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await protectedAxios.get("/v2/subscription/stats", {
        params: buildQueryParams(),
      });

      const payload = response?.data?.data || {};

      setData(payload);
      if (payload.pagination) {
        setPagination(payload.pagination);
      } else {
        setPagination((prev) => ({
          ...prev,
          page,
          limit,
          total: payload.items?.length || 0,
          totalPages: 1,
        }));
      }
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Unable to load subscriptions";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterInputChange = (event) => {
    const { name, value } = event.target;
    setFormFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setAppliedFilters(formFilters);
    setPage(1);
  };

  const resetFilters = () => {
    setFormFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setPage(1);
  };

  const handlePageChange = ({ selected }) => {
    setPage(selected + 1);
  };

  const handleLimitChange = (event) => {
    setLimit(Number(event.target.value));
    setPage(1);
  };

  const formatAmount = (value) => {
    if (value === undefined || value === null || Number.isNaN(value)) {
      return "—";
    }
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    const allItems = data?.items || [];
    if (!debouncedSearchQuery.trim()) {
      return allItems;
    }

    const searchTerm = debouncedSearchQuery.toLowerCase().trim();
    return allItems.filter((item) => {
      // Search in subscriber name
      const subscriberName = `${item?.subscriberId?.firstName || ""} ${
        item?.subscriberId?.lastName || ""
      }`.toLowerCase();
      
      // Search in subscriber email
      const subscriberEmail = (item?.subscriberId?.email || "").toLowerCase();
      
      // Search in organization name
      const orgName = (item?.organizationId?.businessName || 
        `${item?.organizationId?.firstName || ""} ${item?.organizationId?.lastName || ""}`.trim() || 
        "").toLowerCase();
      
      // Search in organization email
      const orgEmail = (item?.organizationId?.email || "").toLowerCase();
      
      // Search in status
      const status = (item?.status || "").toLowerCase();
      
      // Search in payment method
      const paymentMethod = (item?.paymentMethod || "").toLowerCase();
      
      // Search in frequency
      const frequency = (item?.frequency || "").toLowerCase();
      
      // Search in subscriber ID
      const subscriberId = (item?.subscriberId?._id || "").toLowerCase();
      
      // Search in organization ID
      const organizationId = (item?.organizationId?._id || "").toLowerCase();

      return (
        subscriberName.includes(searchTerm) ||
        subscriberEmail.includes(searchTerm) ||
        orgName.includes(searchTerm) ||
        orgEmail.includes(searchTerm) ||
        status.includes(searchTerm) ||
        paymentMethod.includes(searchTerm) ||
        frequency.includes(searchTerm) ||
        subscriberId.includes(searchTerm) ||
        organizationId.includes(searchTerm)
      );
    });
  }, [data?.items, debouncedSearchQuery]);

  const items = filteredItems;

  return (
    <div className="subscription-page">
      <div className="subscription-page__header">
        <div>
          <p className="eyebrow">Billing</p>
          <h1>Subscriptions</h1>
          <p className="subtitle">
            Monitor recurring donations, filter by organizations or donors, and
            inspect detailed subscription records.
          </p>
        </div>
        <div className="subscription-page__actions" style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => setIsSearchModalOpen(true)}
            className="subscription-search-icon-btn"
            aria-label="Search subscriptions"
          >
            <FaSearch />
          </button>
          <button
            type="button"
            onClick={() => setIsFilterModalOpen(true)}
            className="primary-btn"
            style={{ padding: "10px 20px" }}
          >
            Filter
          </button>
          <label className="field-label">
            Rows per page
            <select value={limit} onChange={handleLimitChange}>
              {limitOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="subscription-summary-grid">
        {loading ? (
          <>
            <article className="subscription-card subscription-card-skeleton">
              <div className="skeleton-subscription-label"></div>
              <div className="skeleton-subscription-value"></div>
            </article>
            <article className="subscription-card subscription-card-skeleton">
              <div className="skeleton-subscription-label"></div>
              <div className="skeleton-subscription-value"></div>
            </article>
            <article className="subscription-card subscription-card-skeleton">
              <div className="skeleton-subscription-label"></div>
              <div className="skeleton-subscription-value"></div>
            </article>
          </>
        ) : (
          <>
            <article className="subscription-card">
              <p>
                <FaUsers className="subscription-card-icon" />
                Total subscriptions
              </p>
              <h2>{data?.totalSubscriptions ?? "—"}</h2>
            </article>
            <article className="subscription-card">
              <p>
                <FaCheckCircle className="subscription-card-icon" />
                Active subscriptions
              </p>
              <h2>{data?.activeSubscriptions ?? "—"}</h2>
            </article>
            <article className="subscription-card">
              <p>
                <FaDollarSign className="subscription-card-icon" />
                Total revenue
              </p>
              <h2>{formatAmount(data?.totalRevenue)}</h2>
            </article>
          </>
        )}
      </div>

      {data?.breakdown?.length > 0 && (
        <div className="subscription-breakdown shadow-box">
          <h3>Status breakdown</h3>
          <div className="status-grid">
            {data.breakdown.map((item) => (
              <div key={item?._id || "unknown"} className="status-pill">
                <strong>{item?._id || "Unknown"}</strong>
                <span>{item?.count ?? 0} subs</span>
                <span>Amount: {formatAmount(item?.totalAmount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search Modal */}
      {isSearchModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            paddingTop: "100px",
            zIndex: 1000,
          }}
          onClick={() => setIsSearchModalOpen(false)}
        >
          <div
            style={{
              backgroundColor: "#1a1a1a",
              borderRadius: "12px",
              padding: "30px",
              maxWidth: "600px",
              width: "90%",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2 style={{ margin: 0, color: "#ffffff" }}>Search Subscriptions</h2>
              <button
                type="button"
                onClick={() => setIsSearchModalOpen(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "rgba(255, 255, 255, 0.6)",
                  fontSize: "24px",
                  cursor: "pointer",
                  padding: "0",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaTimes />
              </button>
            </div>
            <div className="subscription-search-container-modal">
              <FaSearch className="subscription-search-icon-modal" />
              <input
                type="text"
                className="subscription-search-input-modal"
                placeholder="Search by name, email, status, payment method..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              {searchQuery && (
                <button
                  type="button"
                  className="subscription-search-clear-modal"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                >
                  <FaTimes />
                </button>
              )}
            </div>
            {searchQuery && (
              <p style={{ marginTop: "12px", fontSize: "13px", color: "rgba(255, 255, 255, 0.6)" }}>
                Searching for: <strong style={{ color: "#ffffff" }}>{debouncedSearchQuery}</strong>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {isFilterModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
          onClick={() => setIsFilterModalOpen(false)}
        >
          <div
            style={{
              backgroundColor: "#1a1a1a",
              borderRadius: "12px",
              padding: "30px",
              maxWidth: "800px",
              width: "90%",
              maxHeight: "90vh",
              overflowY: "auto",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2 style={{ margin: 0, color: "#f8fafc" }}>Filter Subscriptions</h2>
              <button
                type="button"
                onClick={() => setIsFilterModalOpen(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#f8fafc",
                  fontSize: "24px",
                  cursor: "pointer",
                  padding: "0",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ×
              </button>
            </div>

            <form
              onSubmit={(e) => {
                applyFilters(e);
                setIsFilterModalOpen(false);
              }}
            >
              <div className="filter-grid">
                <label className="field-label">
                  Status
                  <select
                    name="status"
                    value={formFilters.status}
                    onChange={handleFilterInputChange}
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="field-label">
                  Payment method
                  <input
                    type="text"
                    name="paymentMethod"
                    placeholder="e.g. card"
                    value={formFilters.paymentMethod}
                    onChange={handleFilterInputChange}
                  />
                </label>

                <label className="field-label">
                  Subscriber ID
                  <input
                    type="text"
                    name="subscriberId"
                    placeholder="User ID"
                    value={formFilters.subscriberId}
                    onChange={handleFilterInputChange}
                  />
                </label>

                <label className="field-label">
                  Organization ID
                  <input
                    type="text"
                    name="organizationId"
                    placeholder="Organization ID"
                    value={formFilters.organizationId}
                    onChange={handleFilterInputChange}
                  />
                </label>

                <label className="field-label">
                  Start date
                  <input
                    type="date"
                    name="startDate"
                    value={formFilters.startDate}
                    onChange={handleFilterInputChange}
                  />
                </label>

                <label className="field-label">
                  End date
                  <input
                    type="date"
                    name="endDate"
                    value={formFilters.endDate}
                    onChange={handleFilterInputChange}
                  />
                </label>

                <label className="field-label">
                  Min amount
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    name="minAmount"
                    placeholder="0.00"
                    value={formFilters.minAmount}
                    onChange={handleFilterInputChange}
                  />
                </label>

                <label className="field-label">
                  Max amount
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    name="maxAmount"
                    placeholder="0.00"
                    value={formFilters.maxAmount}
                    onChange={handleFilterInputChange}
                  />
                </label>
              </div>

              <div className="filter-actions">
                <button type="submit" className="primary-btn">
                  Apply filters
                </button>
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={() => {
                    resetFilters();
                    setIsFilterModalOpen(false);
                  }}
                  disabled={!hasActiveFilters}
                >
                  Clear filters
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="shadow-box admin-box subscription-table">
        <div className="subscription-table__meta">
          <div>
            <h3>Subscriptions</h3>
            <p>
              Showing {items.length} of {pagination.total} results
              {debouncedSearchQuery && (
                <span className="subscription-search-results">
                  {" "}for "{debouncedSearchQuery}"
                </span>
              )}
            </p>
          </div>
          {error && <p className="error-text">{error}</p>}
        </div>

        {loading ? (
          <div className="subscription-table__empty">Loading subscriptions…</div>
        ) : !items.length ? (
          <div className="subscription-table__empty">
            {error || "No subscriptions found for the selected filters."}
          </div>
        ) : (
          <div className="table">
            <table style={{ textAlign: "left" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", whiteSpace: "nowrap" }}>Subscriber</th>
                  <th style={{ textAlign: "left", whiteSpace: "nowrap" }}>Organization</th>
                  <th style={{ textAlign: "left", whiteSpace: "nowrap" }}>Status</th>
                  <th style={{ textAlign: "left", whiteSpace: "nowrap" }}>Payment</th>
                  <th style={{ textAlign: "left", whiteSpace: "nowrap" }}>Frequency</th>
                  <th style={{ textAlign: "left", whiteSpace: "nowrap" }}>Amount</th>
                  <th style={{ textAlign: "left", whiteSpace: "nowrap" }}>Total Paid</th>
                  <th style={{ textAlign: "left", whiteSpace: "nowrap" }}>Created</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item?._id}>
                    <td style={{ textAlign: "left" }}>
                      <span className="table-title">
                        {item?.subscriberId
                          ? `${item.subscriberId.firstName || ""} ${
                              item.subscriberId.lastName || ""
                            }`.trim() || item.subscriberId.email
                          : "—"}
                      </span>
                      <small>{item?.subscriberId?.email || "—"}</small>
                    </td>
                    <td style={{ textAlign: "left" }}>
                      <span className="table-title">
                        {item?.organizationId?.businessName ||
                          `${item?.organizationId?.firstName || ""} ${
                            item?.organizationId?.lastName || ""
                          }`.trim() ||
                          "—"}
                      </span>
                      <small>{item?.organizationId?.email || "—"}</small>
                    </td>
                    <td className="status-cell" style={{ textAlign: "left" }}>{item?.status || "—"}</td>
                    <td style={{ textAlign: "left" }}>{item?.paymentMethod || "—"}</td>
                    <td style={{ textAlign: "left" }}>{item?.frequency || "—"}</td>
                    <td style={{ textAlign: "left" }}>{formatAmount(item?.amount)}</td>
                    <td style={{ textAlign: "left" }}>{formatAmount(item?.totalAmountPaid)}</td>
                    <td style={{ textAlign: "left" }}>
                      {item?.createdAt
                        ? moment(item.createdAt).format("DD MMM YYYY, HH:mm")
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination.totalPages > 1 && (
          <ReactPaginate
            previousLabel={"Previous"}
            nextLabel={"Next"}
            breakLabel={"..."}
            pageCount={Math.max(pagination.totalPages || 0, 1)}
            marginPagesDisplayed={1}
            pageRangeDisplayed={3}
            onPageChange={handlePageChange}
            forcePage={Math.max(0, (pagination.page || 1) - 1)}
            containerClassName={"pagination"}
            activeClassName={"active"}
          />
        )}
      </div>
    </div>
  );

};

export default Subscription;

